import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define the context type
interface MaintenanceContextType {
  isMaintenanceMode: boolean;
  setMaintenanceMode: (isActive: boolean) => void;
}

// Create the context with a default value
const MaintenanceContext = createContext<MaintenanceContextType>({
  isMaintenanceMode: false,
  setMaintenanceMode: () => {},
});

// Create a provider component
interface MaintenanceProviderProps {
  children: ReactNode;
}

export const MaintenanceProvider: React.FC<MaintenanceProviderProps> = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean>(false);

  const setMaintenanceMode = (isActive: boolean) => {
    setIsMaintenanceMode(isActive);
  };

  return (
    <MaintenanceContext.Provider value={{ isMaintenanceMode, setMaintenanceMode }}>
      {children}
    </MaintenanceContext.Provider>
  );
};

// Create a custom hook to use the maintenance context
export const useMaintenance = () => useContext(MaintenanceContext);

// Export a function that can be called from outside React components
let maintenanceModeSetter: ((isActive: boolean) => void) | null = null;

// Synchronous latch to reflect maintenance state immediately (outside React render cycle)
let maintenanceActive = false;

// Getter for synchronous maintenance state (useful before React state updates propagate)
export const isMaintenanceActive = () => maintenanceActive;

export const registerMaintenanceSetter = (setter: (isActive: boolean) => void) => {
  maintenanceModeSetter = setter;
};

export const triggerMaintenanceMode = (isActive: boolean) => {
  // Flip the synchronous latch first to block navigations immediately
  maintenanceActive = isActive;
  if (maintenanceModeSetter) {
    maintenanceModeSetter(isActive);
  }
};

export default MaintenanceContext;
